import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { produce } from 'immer';
import { Cause, Effect, Exit, Match, Option, pipe } from 'effect';
import { Map as IMap, Set as ISet } from 'immutable';

import { jeInvoiceTypeSelector } from '@/domain/journal/selectors';
import { rtlEnable } from '@/domain/env/envSelector';
import { jeFieldRearrangementApi } from '@/domain/jeFieldRearrangement/api';
import { JE_HEADERS_CONFIG_TYPE } from '@/domain/jeFieldRearrangement/schemas';
import { getConfigName } from '@/domain/jeFieldRearrangement/types';
import type { JeHeadersConfigsByName, JeHeadersConfig, SwapCellsPayload } from '@/domain/jeFieldRearrangement/types';
import {
  EMPTY_CONFIG,
  hideCell,
  resetConfig,
  showCell,
  swapCells,
  type ConfigEdit,
} from '@/domain/jeFieldRearrangement/edits';
import { jeFieldRearrangementKeys } from '@/domain/jeFieldRearrangement/queries/keys';
import {
  CompanyChanged,
  ConfigNotLoaded,
  ConfigsAnswerHasNoList,
  EarlierSaveFailed,
  sendConfigsRequest,
  type LoadError,
  type SaveError,
} from '@/domain/jeFieldRearrangement/errors';
import { runEffectAsPromise } from '@/lib/effectHelpers';
import { getCompanyId } from '@/services/api/companyId';
import { showApiErrorToast } from '@/services/api/errorToast';
import { type EndpointResponse } from '@/services/api/endpointTypes';
import { queryOptions } from '@/services/query/queryOptions';

type LoadConfigsResponse = EndpointResponse<typeof jeFieldRearrangementApi.getConfigs>;

const fillMissingConfigFields = (config: Partial<JeHeadersConfig> | null | undefined) => ({
  ...config,
  positions: config?.positions ?? {},
  hidden: config?.hidden ?? [],
  shown: config?.shown ?? [],
  locked: config?.locked ?? [],
});

const groupConfigsByName = (
  response: LoadConfigsResponse,
): Effect.Effect<JeHeadersConfigsByName, ConfigsAnswerHasNoList> =>
  Array.isArray(response?.result)
    ? Effect.succeed(
        Object.fromEntries(response.result.map((item) => [item.name, fillMissingConfigFields(item.config)])),
      )
    : Effect.fail(new ConfigsAnswerHasNoList());

type SaveQueue = { lastQueuedSave: Promise<unknown>; failedSaveCount: number };
const saveQueues = new Map<string, SaveQueue>();

// A promise chain, not Effect's Semaphore: the semaphore lets a newcomer take a freed permit
// before the waiters, so a load could read the server before a queued save is sent.
const getOrCreateCompanySaveQueue = (companyId: string): SaveQueue => {
  const existing = saveQueues.get(companyId);
  if (existing) return existing;
  const created: SaveQueue = { lastQueuedSave: Promise.resolve(), failedSaveCount: 0 };
  saveQueues.set(companyId, created);
  return created;
};

const addSaveToQueue = <A>(companyId: string, save: Effect.Effect<A, SaveError>): Effect.Effect<A, SaveError> =>
  Effect.suspend(() => {
    const queue = getOrCreateCompanySaveQueue(companyId);
    const failedSaveCount = queue.failedSaveCount;
    const queuedSave = queue.lastQueuedSave.then(() =>
      Effect.runPromiseExit(
        queue.failedSaveCount === failedSaveCount
          ? save.pipe(
              Effect.tapErrorTag('ConfigsRequestFailed', () =>
                Effect.sync(() => {
                  queue.failedSaveCount += 1;
                }),
              ),
            )
          : Effect.fail(new EarlierSaveFailed()),
      ),
    );
    queue.lastQueuedSave = queuedSave;
    return Effect.flatMap(
      Effect.promise(() => queuedSave),
      (exit) => exit,
    );
  });

export const waitForQueuedSaves = (companyId: string): Effect.Effect<void> =>
  Effect.promise(() => getOrCreateCompanySaveQueue(companyId).lastQueuedSave).pipe(Effect.asVoid);

const knownServerConfigs = new Map<string, JeHeadersConfigsByName>();

const loadConfigs = (companyId: string, signal?: AbortSignal): Effect.Effect<JeHeadersConfigsByName, LoadError> =>
  Effect.gen(function* () {
    // otherwise the load reads the server before an edit being saved and overwrites it in the cache
    yield* waitForQueuedSaves(companyId);
    // requests name their company by the CompanyId header read when sent, not by this key
    if (getCompanyId() !== companyId) return yield* new CompanyChanged();
    const response = yield* sendConfigsRequest(() =>
      jeFieldRearrangementApi.getConfigs({ query: { type: JE_HEADERS_CONFIG_TYPE } }),
    );
    const configs = yield* groupConfigsByName(response);
    if (!signal?.aborted) knownServerConfigs.set(companyId, configs);
    return configs;
  });

export const jeHeadersConfigsQuery = (companyId: string) =>
  queryOptions({
    queryKey: jeFieldRearrangementKeys.configs(companyId),
    queryFn: ({ signal }) =>
      runEffectAsPromise(
        loadConfigs(companyId, signal).pipe(
          // the query client's retry policy reads the API client's error itself
          Effect.catchTag('ConfigsRequestFailed', ({ originalError }) => Effect.fail(originalError)),
        ),
      ),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

const isLoadedAndNotReloading = (queryClient: QueryClient, companyId: string) => {
  const state = queryClient.getQueryState(jeFieldRearrangementKeys.configs(companyId));
  return state?.status === 'success' && state.fetchStatus === 'idle';
};

type SaveTarget = { companyId: string; configName: string };

const sendCachedConfig = (queryClient: QueryClient, { companyId, configName }: SaveTarget) =>
  Effect.gen(function* () {
    // the configs API answers a save naming another company with 403, which signs the user out
    if (getCompanyId() !== companyId) return yield* new CompanyChanged();
    const config =
      queryClient.getQueryData<JeHeadersConfigsByName>(jeFieldRearrangementKeys.configs(companyId))?.[configName] ??
      EMPTY_CONFIG;
    const response = yield* sendConfigsRequest(() =>
      jeFieldRearrangementApi.saveConfig({
        query: { type: JE_HEADERS_CONFIG_TYPE },
        body: { name: configName, company_id: companyId, type: JE_HEADERS_CONFIG_TYPE, config },
      }),
    );
    knownServerConfigs.set(companyId, { ...knownServerConfigs.get(companyId), [configName]: config });
    return response;
  });

export const saveConfigFromCache = (queryClient: QueryClient, target: SaveTarget) =>
  Effect.gen(function* () {
    if (!target.configName || !isLoadedAndNotReloading(queryClient, target.companyId)) {
      return yield* new ConfigNotLoaded();
    }
    return yield* addSaveToQueue(target.companyId, sendCachedConfig(queryClient, target));
  });

const restoreKnownServerConfigs = (queryClient: QueryClient, companyId: string) => {
  const known = knownServerConfigs.get(companyId);
  if (known) queryClient.setQueryData<JeHeadersConfigsByName>(jeFieldRearrangementKeys.configs(companyId), known);
  return Boolean(known);
};

export type FormConfigLoadState = 'loaded' | 'loading' | 'failed';

const getLoadState = (query: UseQueryResult<JeHeadersConfigsByName>): FormConfigLoadState => {
  if (query.fetchStatus !== 'idle') return 'loading';
  if (query.isSuccess) return 'loaded';
  return query.isError ? 'failed' : 'loading';
};

export type FieldRearrangementEditor = {
  config: JeHeadersConfig;
  loadState: FormConfigLoadState;
  retry: () => void;
  swap: (payload: SwapCellsPayload) => void;
  /** `aliases`: the field's other names its settings may be stored under (DA-18808). */
  hide: (fieldName: string, aliases?: ReadonlyArray<string>) => void;
  show: (fieldName: string, aliases?: ReadonlyArray<string>) => void;
  reset: () => void;
};

export const useFieldRearrangement = (): FieldRearrangementEditor => {
  const queryClient = useQueryClient();
  const { companyId, documentId } = useParams<{ companyId: string; documentId: string }>();
  const transactionType = useSelector(jeInvoiceTypeSelector) as string;
  const rtl = useSelector(rtlEnable) as boolean;

  const configsQuery = useQuery({ ...jeHeadersConfigsQuery(companyId ?? ''), enabled: Boolean(companyId) });
  const loadState = getLoadState(configsQuery);
  const configName = getConfigName(transactionType, rtl);
  const config = configsQuery.data?.[configName] ?? EMPTY_CONFIG;
  const { refetch } = configsQuery;

  const lastOpened = useRef({ companyId, documentId });
  useEffect(() => {
    const previous = lastOpened.current;
    lastOpened.current = { companyId, documentId };
    if (previous.documentId === documentId || previous.companyId !== companyId) return;
    refetch({ cancelRefetch: false });
  }, [companyId, documentId, refetch]);

  const applyEdit = useCallback(
    (edit: ConfigEdit) => {
      if (!companyId || !transactionType || !isLoadedAndNotReloading(queryClient, companyId)) return;
      const queryKey = jeFieldRearrangementKeys.configs(companyId);
      const configBeforeEdit = queryClient.getQueryData<JeHeadersConfigsByName>(queryKey)?.[configName] ?? EMPTY_CONFIG;
      const writeConfig = (value: JeHeadersConfig) =>
        queryClient.setQueryData<JeHeadersConfigsByName>(
          queryKey,
          produce((draft) => {
            if (!draft) return;
            draft[configName] = value;
          }),
        );
      writeConfig(edit(configBeforeEdit));
      const undoFailedSave = (error: unknown) => {
        if (!restoreKnownServerConfigs(queryClient, companyId)) writeConfig(configBeforeEdit);
        showApiErrorToast(error);
        queryClient.invalidateQueries({ queryKey });
      };
      const handleSaveError = Match.type<SaveError>().pipe(
        Match.tag('EarlierSaveFailed', () => undefined),
        Match.tag('ConfigNotLoaded', () => writeConfig(configBeforeEdit)),
        Match.tag('CompanyChanged', () => queryClient.removeQueries({ queryKey })),
        Match.tag('ConfigsRequestFailed', ({ originalError }) => undoFailedSave(originalError)),
        Match.exhaustive,
      );
      Effect.runPromiseExit(saveConfigFromCache(queryClient, { companyId, configName })).then(
        Exit.match({
          onSuccess: () => undefined,
          onFailure: (cause) =>
            pipe(
              Cause.failureOption(cause),
              Option.match({ onSome: handleSaveError, onNone: () => undoFailedSave(Cause.squash(cause)) }),
            ),
        }),
      );
    },
    [companyId, transactionType, queryClient, configName],
  );

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      config,
      loadState,
      retry,
      swap: (payload) => applyEdit(swapCells(payload)),
      hide: (fieldName, aliases) => applyEdit(hideCell(fieldName, aliases)),
      show: (fieldName, aliases) => applyEdit(showCell(fieldName, aliases)),
      reset: () => applyEdit(resetConfig),
    }),
    [config, loadState, retry, applyEdit],
  );
};

export type HeaderFieldLayout = {
  positions: IMap<string, string>;
  hidden: ISet<string>;
  shown: ISet<string>;
  locked: ISet<string>;
};

const layoutByConfig = new WeakMap<JeHeadersConfig, HeaderFieldLayout>();

const getHeaderFieldLayout = (config: JeHeadersConfig): HeaderFieldLayout => {
  const known = layoutByConfig.get(config);
  if (known) return known;
  const created: HeaderFieldLayout = {
    positions: IMap<string, string>(Object.entries(config.positions)),
    hidden: ISet<string>(config.hidden),
    shown: ISet<string>(config.shown),
    locked: ISet<string>(config.locked),
  };
  layoutByConfig.set(config, created);
  return created;
};

export const useHeaderFieldLayout = (): HeaderFieldLayout => {
  const { companyId } = useParams<{ companyId: string }>();
  const transactionType = useSelector(jeInvoiceTypeSelector) as string;
  const rtl = useSelector(rtlEnable) as boolean;
  const { data } = useQuery({ ...jeHeadersConfigsQuery(companyId ?? ''), enabled: false });
  return getHeaderFieldLayout(data?.[getConfigName(transactionType, rtl)] ?? EMPTY_CONFIG);
};
